import * as React from "react";
import { useMsal } from "@azure/msal-react";
import { loadGapiInsideDOM } from "gapi-script";
import { GoogleLogin } from "react-google-login";
import { DefaultButton, Dialog, DialogType, MessageBar, MessageBarType, Separator, Spinner, SpinnerSize, Stack, Text } from "office-ui-fabric-react";
import { PageURL } from "../../App";
import DataProvider from "../../dataprovider/DataProvider";
import { CompanyAccountForm } from "../LoginComponent/CompanyAccountForm";
import { AccountType, IAuthenticatedUser, IUserAccount } from "../LoginComponent/LoginComponent";
import GoogleIcon from "./../../images/iconGoogle.png";
import Styles from "./Welcome.scss";

const GOOGLE_CLIENT_ID = "313235099716-c5ttur4f2v0o001gd3vngd55kmhirihm.apps.googleusercontent.com";

const microsoftLoginRequest = {
  scopes: ["User.Read"],
};

const MICROSOFT_ICON_SVG =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMSIgaGVpZ2h0PSIyMSIgdmlld0JveD0iMCAwIDIxIDIxIj48cmVjdCB4PSIxIiB5PSIxIiB3aWR0aD0iOSIgaGVpZ2h0PSI5IiBmaWxsPSIjZjI1MDIyIi8+PHJlY3QgeD0iMSIgeT0iMTEiIHdpZHRoPSI5IiBoZWlnaHQ9IjkiIGZpbGw9IiMwMGE0ZWYiLz48cmVjdCB4PSIxMSIgeT0iMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iIzdmYmEwMCIvPjxyZWN0IHg9IjExIiB5PSIxMSIgd2lkdGg9IjkiIGhlaWdodD0iOSIgZmlsbD0iI2ZmYjkwMCIvPjwvc3ZnPg==";

export interface ISignInDialogProps {
  isOpen: boolean;
  onDismiss: () => void;
  history: any;
  dataProvider: DataProvider;
  setAuthStatus: (loggedIn: boolean, user: IAuthenticatedUser) => Promise<void>;
}

const parseMicrosoftUser = (account: any): IUserAccount => ({
  firstName: account?.idTokenClaims?.given_name,
  lastName: account?.idTokenClaims?.family_name,
  email: account?.idTokenClaims?.email,
  accountType: AccountType.Microsoft,
  userId: "",
  activateGuid: null,
  id: null,
  password: "",
});

export const SignInDialog = (props: ISignInDialogProps): React.ReactElement => {
  const { instance } = useMsal();
  const [isBusy, setIsBusy] = React.useState<boolean>(false);
  const [busyLabel, setBusyLabel] = React.useState<string>("");
  const [errorMessage, setErrorMessage] = React.useState<string>("");

  const navigateToCreateAccount = React.useCallback(() => {
    props.onDismiss();
    props.history.push(PageURL.CreateAccount);
  }, [props]);

  const completeExternalAuthentication = React.useCallback(
    async (user: IUserAccount, token: string, providerLabel: string) => {
      setErrorMessage("");
      setBusyLabel(`Checking ${providerLabel} account...`);
      setIsBusy(true);

      try {
        const authenticatedUser = await props.dataProvider.ensureExternalUserAccount(user, token, false);

        if (authenticatedUser.isNewUser) {
          window.sessionStorage.rsmConsentTarget = JSON.stringify(authenticatedUser);
          window.sessionStorage.removeItem("authToken");
          window.sessionStorage.removeItem("stlUser");
          props.onDismiss();
          props.history.push(PageURL.Consent);
          return;
        }

        await props.setAuthStatus(true, authenticatedUser);
        props.onDismiss();
      } catch (error) {
        window.sessionStorage.removeItem("authToken");
        window.sessionStorage.removeItem("stlUser");
        setErrorMessage(`Authentication with ${providerLabel} was not possible. The returned token was invalid.`);
      } finally {
        setIsBusy(false);
        setBusyLabel("");
      }
    },
    [props],
  );

  const handleGoogleSuccess = React.useCallback(
    (response: any) => {
      if (!response?.profileObj || !response?.tokenId) {
        setErrorMessage("Google sign-in did not return a usable profile.");
        return;
      }

      const user: IUserAccount = {
        firstName: response.profileObj.givenName,
        lastName: response.profileObj.familyName,
        email: response.profileObj.email,
        accountType: AccountType.Google,
        userId: "",
        activateGuid: null,
        id: null,
        password: "",
      };

      void completeExternalAuthentication(user, response.tokenId, "Google");
    },
    [completeExternalAuthentication],
  );

  const handleGoogleFailure = React.useCallback((response: any) => {
    if (response?.error === "popup_closed_by_user") {
      return;
    }

    setErrorMessage(response?.details || response?.error || "Google sign-in was not completed.");
  }, []);

  const handleMicrosoftSignIn = React.useCallback(() => {
    setErrorMessage("");
    instance
      .loginPopup(microsoftLoginRequest)
      .then((result: any) => {
        const user = parseMicrosoftUser(result?.account);
        return completeExternalAuthentication(user, result.idToken, "Microsoft");
      })
      .catch(() => {
        setErrorMessage("Microsoft sign-in was cancelled or could not be completed.");
      });
  }, [completeExternalAuthentication, instance]);

  const forceGoogleLogout = React.useCallback(() => {
    loadGapiInsideDOM()
      .then((gapi) => {
        gapi.load("auth2", function () {
          try {
            const auth2 = gapi.auth2?.getAuthInstance?.();

            if (auth2 && typeof auth2.signOut === "function") {
              void auth2.signOut();
            }
          } catch {
            return;
          }
        });
      })
      .catch(() => {
        return;
      });
  }, []);

  React.useEffect(() => {
    if (!props.isOpen) {
      setErrorMessage("");
      setBusyLabel("");
      setIsBusy(false);
    }
  }, [props.isOpen]);

  React.useEffect(() => {
    if (errorMessage && errorMessage.toLowerCase().indexOf("google") !== -1) {
      forceGoogleLogout();
    }
  }, [errorMessage, forceGoogleLogout]);

  return (
    <Dialog
      hidden={!props.isOpen}
      onDismiss={props.onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: "Sign in",
        closeButtonAriaLabel: "Close",
        showCloseButton: true,
      }}
      modalProps={{
        isBlocking: false,
        className: Styles.welcomeThemeDialog,
        styles: {
          main: {
            maxWidth: "410px !important",
            width: "calc(100% - 32px)",
          },
        },
      }}
    >
      <Stack tokens={{ childrenGap: 16 }}>
        <Text variant="mediumPlus">Choose a sign-in method to access the STL library.</Text>

        {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}

        <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
          <GoogleLogin
            clientId={GOOGLE_CLIENT_ID}
            buttonText="Continue with Google"
            onSuccess={handleGoogleSuccess}
            onFailure={handleGoogleFailure}
            cookiePolicy="single_host_origin"
            render={(renderProps: any) => (
              <DefaultButton
                onClick={renderProps.onClick}
                disabled={renderProps.disabled || isBusy}
                className={Styles.welcomeDialogProviderButton}
                style={{ width: "100%", justifyContent: "left", display: "flex" }}
              >
                <span className={Styles.welcomeDialogProviderButtonInner}>
                  <img src={GoogleIcon} alt="Google" className={Styles.welcomeDialogProviderImage} style={{ width: 35, paddingRight: 10 }} />
                  <span>Continue with Google</span>
                </span>
              </DefaultButton>
            )}
          />
          <DefaultButton
            onClick={handleMicrosoftSignIn}
            disabled={isBusy}
            className={Styles.welcomeDialogProviderButton}
            style={{ width: "100%", justifyContent: "left", display: "flex" }}
          >
            <span className={Styles.welcomeDialogProviderButtonInner}>
              <img src={MICROSOFT_ICON_SVG} alt="Microsoft" className={Styles.welcomeDialogProviderImage} style={{ width: 35, paddingRight: 10 }} />
              <span>Continue with Microsoft</span>
            </span>
          </DefaultButton>
        </Stack>

        {isBusy && <Spinner size={SpinnerSize.medium} label={busyLabel || "Working on it..."} ariaLive="assertive" />}

        <Separator>Company account</Separator>

        <CompanyAccountForm
          mode="login"
          dataProvider={props.dataProvider}
          setAuthStatus={props.setAuthStatus}
          onAuthenticated={() => {
            props.onDismiss();
          }}
          onNavigateToCreateAccount={navigateToCreateAccount}
        />

        <Text variant="small" className={Styles.welcomeDialogFooterText} style={{ paddingBottom: 12 }}>
          By signing in, you continue to the existing Straumann authentication and consent flow without exposing credentials outside the configured providers.
        </Text>
      </Stack>
    </Dialog>
  );
};
